import React, { useState } from "react";
import { ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { HID } from "@/constants/cosecIds";

const TESTIMONIALS = [
  {
    q: "A reliable platform for businesses that need stronger compliance controls.",
    name: "Admin Lead",
    role: "Bristol, UK",
  },
  {
    q: "CoSec gives our team a clearer way to manage deadlines, records, and compliance without unnecessary admin.",
    name: "Operations Manager",
    role: "London SME",
  },
  {
    q: "We moved away from spreadsheets and now have better visibility across filings and governance tasks.",
    name: "Company Secretary",
    role: "UK Services Firm",
  },
  {
    q: "Simple to use, secure, and ideal for keeping company secretarial work organised.",
    name: "Finance Director",
    role: "Manchester",
  },
  {
    q: "The reminders and centralised records save our team valuable time every month.",
    name: "Company Secretary",
    role: "Leeds, UK",
  },
];

export default function Testimonials() {
  const [idx, setIdx] = useState(0);
  const total = TESTIMONIALS.length;
  const prev = () => setIdx((idx - 1 + total) % total);
  const next = () => setIdx((idx + 1) % total);

  return (
    <section className="py-20 md:py-28 bg-secondary/40 border-y border-border">
      <div className="container-cv">
        <div className="max-w-3xl">
          <span className="section-eyebrow">Why businesses choose CoSec</span>
          <h2 className="mt-4 font-display text-3xl md:text-5xl font-bold text-primary tracking-tight leading-[1.08]">
            Trusted by company secretaries, accountants &amp;{" "}
            <span className="text-accent">growing businesses.</span>
          </h2>
        </div>

        <div className="mt-12 grid lg:grid-cols-12 gap-6">
          {/* Featured testimonial */}
          <div className="lg:col-span-7">
            <div className="relative rounded-3xl bg-primary text-primary-foreground p-8 md:p-12 overflow-hidden">
              <div className="absolute inset-0 bg-dotgrid opacity-[0.06] pointer-events-none" />
              <div className="absolute -top-20 -right-20 h-64 w-64 rounded-full bg-accent/20 blur-3xl pointer-events-none" />
              <Quote className="h-12 w-12 text-accent" />
              <blockquote className="mt-4 font-display text-xl md:text-2xl font-bold leading-snug">
                “{TESTIMONIALS[idx].q}”
              </blockquote>
              <div className="mt-6 flex items-center justify-between gap-4">
                <div>
                  <div className="font-display font-bold text-white">{TESTIMONIALS[idx].name}</div>
                  <div className="text-xs text-white/60">{TESTIMONIALS[idx].role}</div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    data-testid={HID.testimonialPrev}
                    onClick={prev}
                    aria-label="Previous testimonial"
                    className="h-10 w-10 grid place-items-center rounded-full border border-white/15 hover:border-accent hover:text-accent transition-colors"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <button
                    data-testid={HID.testimonialNext}
                    onClick={next}
                    aria-label="Next testimonial"
                    className="h-10 w-10 grid place-items-center rounded-full bg-accent text-primary hover:bg-accent/90 transition-colors"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Side testimonials */}
          <div className="lg:col-span-5 grid grid-cols-1 gap-4">
            {TESTIMONIALS.filter((_, i) => i !== idx).slice(0, 3).map((t, i) => (
              <div
                key={`${t.name}-${i}`}
                data-testid={HID.testimonialItem(i + 1)}
                className="rounded-2xl border border-border bg-white p-5"
              >
                <Quote className="h-5 w-5 text-accent" />
                <p className="mt-2 text-sm text-primary/90 leading-relaxed">“{t.q}”</p>
                <div className="mt-3 flex items-center justify-between">
                  <div className="text-xs">
                    <div className="font-display font-bold text-primary">{t.name}</div>
                    <div className="text-muted-foreground">{t.role}</div>
                  </div>
                  <div className="text-[10px] font-mono text-muted-foreground">★★★★★</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
